import { useEffect, useState } from "react";
import type { ChartToothStateName, ToothGlyphName } from "@dentacore/shared-types";

import { getToothChart } from "../api/patients";
import { ToothChart } from "../patients/tooth-chart";
import { Modal } from "../ui/modal";
import styles from "./calendar.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * Which teeth a booking is for, chosen on the patient's own chart — 2026-09-24.
 *
 * Anthony: *"When picking which tooth, open a popup of the tooth chart."* A
 * list of 32 FDI numbers asked a receptionist to translate "upper right six"
 * into "16"; the chart is how the practice already thinks about a mouth, and
 * this one is **the patient's**, drawn in its current colours, so the tooth
 * that is already red, done or missing is visible before it is chosen.
 *
 * **Several teeth at once** (BF6, the same day): *"when u select one, highlight
 * it in red, and add a 'done' button next to the cancel one."* Clicking a tooth
 * toggles it, and a chosen tooth is drawn red — the colour it will have on the
 * chart once booked — with the heavier selected outline so it still reads as
 * chosen on a tooth that was red already. **Done** keeps the choice; **Cancel**
 * throws it away and leaves the booking's teeth as they were.
 *
 * The field reads as a button — *"#14, #16 · change"*, or *"Choose teeth"*.
 *
 * **One tooth, for the Complete dialog** (Emile's QA, 2026-10-18: *"when
 * selecting a tooth, the teeth chart should show up"*). `single` makes a click
 * choose that tooth and close the chart — one line of a finished visit is one
 * tooth, so there is nothing to collect and no Done to press.
 */
export function ToothPicker({
  patientId,
  value,
  onChange,
  required,
  disabled,
  single = false,
  label: fieldLabel,
  testId = "chain-tooth",
}: {
  patientId: string;
  /** The chosen FDI numbers, in chart order; empty for none. */
  value: readonly number[];
  onChange: (teeth: number[]) => void;
  /** A multi-visit procedure cannot start its course without one (TP12). */
  required: boolean;
  disabled?: boolean;
  /** Exactly one tooth: a click chooses it and closes the chart. */
  single?: boolean;
  /** The field's label; "Which teeth" when not given. */
  label?: string;
  testId?: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  // What is ticked in the dialog, committed only by Done.
  const [draft, setDraft] = useState<number[]>([]);
  const [states, setStates] = useState<Partial<Record<number, ChartToothStateName>>>({});
  const [glyphs, setGlyphs] = useState<Partial<Record<number, readonly ToothGlyphName[]>>>({});
  // Follows the patient's own setting (BT8): a baby tooth can be booked.
  const [primaryTeeth, setPrimaryTeeth] = useState(false);

  // The patient's chart, read when the dialog opens — so it is current.
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    getToothChart(patientId, controller.signal)
      .then((chart) => {
        if (controller.signal.aborted) return;
        setPrimaryTeeth(chart.primaryTeeth);
        setStates(Object.fromEntries(chart.teeth.map((tooth) => [tooth.fdiNumber, tooth.state])));
        setGlyphs(Object.fromEntries(chart.teeth.map((tooth) => [tooth.fdiNumber, tooth.glyphs])));
      })
      .catch(() => {
        // An unreadable chart still lets teeth be chosen: every tooth draws
        // sound, which is what an empty chart looks like anyway.
      });
    return () => controller.abort();
  }, [open, patientId]);

  const openPicker = (): void => {
    setDraft([...value]);
    setOpen(true);
  };

  const toggle = (fdi: number): void => {
    if (single) {
      onChange([fdi]);
      setOpen(false);
      return;
    }
    setDraft((current) =>
      current.includes(fdi)
        ? current.filter((tooth) => tooth !== fdi)
        : [...current, fdi].sort((a, b) => a - b),
    );
  };

  // A chosen tooth draws red — what it will be once booked.
  const shown: Partial<Record<number, ChartToothStateName>> = { ...states };
  for (const fdi of draft) shown[fdi] = "TO_BE_DONE";

  const label = value.map((fdi) => `#${String(fdi)}`).join(", ");

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>
        {fieldLabel ?? "Which teeth"}
        {required && <RequiredMark />}
      </span>
      <div className={styles.toothPickRow}>
        <button
          type="button"
          className={styles.toothPick}
          disabled={disabled}
          onClick={openPicker}
          aria-haspopup="dialog"
          data-testid={testId}
          data-value={value.join(",")}
        >
          {value.length === 0 ? (single ? "Choose tooth" : "Choose teeth") : `${label} · change`}
        </button>
        {value.length > 0 && !required && (
          <button
            type="button"
            className={styles.linkButton}
            disabled={disabled}
            onClick={() => onChange([])}
            data-testid={`${testId}-clear`}
          >
            No tooth
          </button>
        )}
      </div>

      {open && (
        <Modal
          label={single ? "Choose the tooth" : "Choose teeth"}
          onClose={() => setOpen(false)}
          testId="tooth-picker"
          panelClassName={styles.toothPickerPanel}
        >
          <h2 className={styles.toothPickerTitle}>
            {single ? "Choose the tooth" : "Choose teeth"}
          </h2>
          <ToothChart
            states={shown}
            glyphs={glyphs}
            selectedTeeth={draft}
            onSelectTooth={toggle}
            primaryTeeth={primaryTeeth}
          />
          <div className={styles.toothPickerActions}>
            <span className={styles.fieldHint} data-testid="tooth-picker-count">
              {single || draft.length === 0
                ? "Click a tooth to choose it"
                : `${String(draft.length)} ${draft.length === 1 ? "tooth" : "teeth"} chosen`}
            </span>
            <button type="button" className={styles.secondary} onClick={() => setOpen(false)}>
              Cancel
            </button>
            {!single && (
              <button
                type="button"
                className={styles.primary}
                // A required field cannot be finished empty; clearing an optional
                // one is what "No tooth" beside the field is for, and Done too.
                disabled={required && draft.length === 0}
                onClick={() => {
                  onChange(draft);
                  setOpen(false);
                }}
                data-testid="tooth-picker-done"
              >
                Done
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
